import { Check, Pencil, X } from 'lucide-react-native';
import { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { FormInput, HStack } from '../../../components/primitives';
import { Heading2 } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';

/** Mirrors the server's bound; the agent says this name out loud. */
const MAX_NAME_LENGTH = 120;

interface AgentNameFieldProps {
  name: string;
  onSave: (name: string) => void;
  isSaving?: boolean;
  /** Archived agents are not operated, so they are not renamed either. */
  editable?: boolean;
}

/**
 * The agent's name, editable in place (FLOSEAI-656).
 *
 * Renaming used to be impossible once an agent was published, which left two
 * agents built from the same template permanently indistinguishable in the
 * pickers that decide where a call goes.
 *
 * Editing happens here rather than in the creation wizard on purpose: the
 * wizard is the frozen configuration a published agent may not re-enter, while
 * the name is the one thing a published agent must still be able to change.
 */
export function AgentNameField({ name, onSave, isSaving, editable = true }: AgentNameFieldProps) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const startEditing = () => {
    setDraft(name);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setDraft('');
  };

  const saveDraft = () => {
    // Guarded here and not only through the button's disabled state: a double
    // tap would otherwise fire two renames.
    if (isSaving) return;

    const next = draft.trim();
    // An empty name would leave the agent introducing itself as nothing, and an
    // unchanged one is not worth a round trip.
    if (!next || next === name) {
      cancelEditing();
      return;
    }
    setIsEditing(false);
    setDraft('');
    onSave(next);
  };

  if (!isEditing) {
    return (
      <HStack spacing="xs" align="center" testID="agent_name_field">
        <Heading2 style={styles.name} accessibilityRole="header">
          {name}
        </Heading2>
        {editable && (
          <Pressable
            testID="agent_name_edit"
            onPress={startEditing}
            hitSlop={8}
            style={({ pressed }) => [styles.action, pressed && styles.actionPressed]}
            accessibilityRole="button"
            accessibilityLabel={t('voiceAgents.detail.renameA11y')}
          >
            <Pencil size={16} color={colors.primary.neon} strokeWidth={1.8} />
          </Pressable>
        )}
      </HStack>
    );
  }

  return (
    <HStack spacing="xs" align="center" testID="agent_name_field">
      <View style={{ flex: 1 }}>
        <FormInput
          testID="agent_name_input"
          value={draft}
          onChangeText={setDraft}
          onSubmitEditing={saveDraft}
          editable={!isSaving}
          autoFocus
          maxLength={MAX_NAME_LENGTH}
          accessibilityLabel={t('voiceAgents.detail.renameInputA11y')}
          returnKeyType="done"
          style={styles.input}
        />
      </View>
      <Pressable
        testID="agent_name_save"
        onPress={saveDraft}
        disabled={isSaving}
        hitSlop={8}
        style={({ pressed }) => [
          styles.action,
          pressed && styles.actionPressed,
          isSaving && styles.actionDisabled,
        ]}
        accessibilityRole="button"
        accessibilityState={{ disabled: isSaving }}
        accessibilityLabel={t('voiceAgents.detail.renameSaveA11y')}
      >
        <Check size={16} color={colors.primary[500]} strokeWidth={2} />
      </Pressable>
      <Pressable
        testID="agent_name_cancel"
        onPress={cancelEditing}
        hitSlop={8}
        style={({ pressed }) => [styles.action, pressed && styles.actionPressed]}
        accessibilityRole="button"
        accessibilityLabel={t('voiceAgents.detail.renameCancelA11y')}
      >
        <X size={16} color={colors.text.secondary} strokeWidth={2} />
      </Pressable>
    </HStack>
  );
}

const styles = StyleSheet.create({
  name: { color: colors.text.primary },
  input: {
    fontSize: 18,
    paddingVertical: tokens.spacing.xs,
  },
  action: {
    padding: tokens.spacing.xs,
    borderRadius: tokens.radius.sm,
  },
  actionPressed: { opacity: 0.6 },
  actionDisabled: { opacity: 0.4 },
});
